<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :disabled="suppressPopover"
    :open-on-hover="openOnHover"
    :max-width="maxWidth"
    :location
  >
    <template #activator="{ props }">
      <slot name="customActivator" :props="props">
        <v-icon
          v-if="iconOnly"
          v-bind="props"
          :color
          :size
          tabindex="-1"
        >
          {{ icon }}
        </v-icon>
        <v-chip
          v-else
          v-bind="{ customClosure, ...props }"
          :class="[
            'ma-0',
            'rChip', {
              'is-added': isAdded,
              'is-deleted': isDeleted
            }
          ]"
          :close="closable"
          :prepend-icon="icon"
          :color
          :size
          variant="flat"
          @click:close="messageClosed"
          @click="emitClick"
        >
          <slot name="chip-label">
            <span
              class="chip-text text-white"
              v-bind="{ customClosure, ...props }"
              @click="emitClick"
            >
              {{ chipLabel }}
            </span>
          </slot>
        </v-chip>
      </slot>
    </template>
    <v-card density="compact">
      <v-card-title v-if="title" :class="[`bg-${localTitleColor}`, 'pt-3 pb-1']">
        <span :class="[textColor]">{{ title }}</span>
        <slot name="additionalHeaderActions" />
      </v-card-title>
      <v-list
        density="compact"
        :class="['py-0', { 'list-scroll': maxHeight }]"
        :max-height="maxHeight"
      >
        <v-list-item v-if="justifySpaceBetween && headings && headings.length">
          <v-row no-gutters justify="space-between" class="pa-2">
            <span class="text-h6 text-decoration-underline">{{ headings[0] }}</span>
            <span class="text-h6 text-decoration-underline pl-2">{{ headings[1] }}</span>
          </v-row>
        </v-list-item>
        <template v-for="(message, index) in shownMessages" :key="index">
          <v-list-item :class="{ 'break-word': breakWord }" class="pa-2">
            <v-row v-if="justifySpaceBetween" no-gutters justify="space-between">
              <LinkableMessage class="text-body-1" :message="message[0]" />
              <LinkableMessage class="text-body-1 pl-2" :message="message[1]" />
            </v-row>
            <LinkableMessage v-else class="text-body-1" :message="message" />
          </v-list-item>
          <template v-if="isExtendedMessage(message)">
            <v-list-item v-for="(value, valueIndex) of message.slice(2)" :key="`add_${index}-${valueIndex}`" class="pa-2">
              <v-row no-gutters justify="end">
                <LinkableMessage class="text-body-1 pl-2" :message="value" />
              </v-row>
            </v-list-item>
          </template>
        </template>
      </v-list>
      <slot name="additionalContent" />
      <v-card-actions>
        <slot name="additionalActions" />
        <div class="flex-grow-1" />
        <v-btn text @click="menu = false">Close</v-btn>
        <v-btn
          v-if="clearable"
          color="primary"
          @click="clearMessages"
        >
          Dismiss
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex';
import LinkableMessage from '@/components/core/common/LinkableMessage.vue';

/**
 * Generic and customizable Vuetify chip popover component for displaying custom messages
 * via a configurable Vueitfy chip. When chip is clicked on, a popover will appear displaying
 * supplied messages. Chip and popover can be customized as per supplied props.
 * @property {Boolean} [breakWord=false]         - Enable word wrap for long lines of text
 * @property {Boolean} [clearable=false]         - Indicates that popover should have a button to clear/dismiss messages
 * @property {Boolean} [closable=false]          - Indicates that the chip should have an 'x' icon
 * @property {Boolean} [defaultToOpen=false]     - Defaults the popover to be automatically opened when instantiated
 * @property {String}  [icon=null]               - Icon to be displayed to left of chip text
 * @property {Boolean} [iconOnly=false]          - Indicates icon should be rendered instead of chip
 * @property {Boolean} [isAdded=false]           - Indicates that underlying record has been set for addition
 * @property {Boolean} [isDeleted=false]         - Indicates that underlying record has been set for deletion
 * @property {String}  label                     - Label to be used for chip and popover title
 * @property {Boolean} [large=false]             - Indicates a 'large' Vuetify chip
 * @property {String[]|String} messages          - String or Array of strings to be displayed as messages within popover
 * @property {String} [location='top']           - Location of menu
 * @property {String}  [popoverTitle=null]       - Title to be used for popover's title
 * @property {Boolean} [small=false]             - Indicates a 'small' Vuetify chip
 * @property {Boolean} [suppressPopover=false]   - Suppresses popover and makes chip only
 * @property {String}  [title=null]              - Title to be used within popover header
 * @property {Boolean} [xLarge=false]            - Indicates a 'x-large' Vuetify chip
 * @property {Boolean} [xSmall=false]            - Indicates a 'x-small' Vuetify chip
 */

export default {
  components: {
    LinkableMessage,
  },

  props: {
    breakWord: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    closable: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'primary',
    },
    customActivator: {
      type: Boolean,
      default: false,
    },
    customClosure: {
      type: Boolean,
      default: false,
    },
    defaultToOpen: {
      type: Boolean,
      default: false,
    },
    headings: {
      type: Array,
      default: () => [],
    },
    hover: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: null,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    isAdded: {
      type: Boolean,
      default: false,
    },
    isDeleted: {
      type: Boolean,
      default: false,
    },
    justifySpaceBetween: {
      type: Boolean,
      default: false,
    },
    label: {
      type: null,
      default: '',
    },
    large: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: '40vh',
    },
    maxWidth: {
      type: String,
      default: '550',
    },
    messages: {
      type: [Array, String],
      default: () => [],
    },
    minWidth: {
      type: String,
      default: '450',
    },
    location: {
      type: String,
      default: 'top',
    },
    popoverTitle: {
      type: String,
      default: null,
    },
    small: {
      type: Boolean,
      default: false,
    },
    suppressPopover: {
      type: Boolean,
      default: false,
    },
    textColor: {
      type: String,
      default: 'text-white',
    },
    titleColor: {
      type: String,
      default: null,
    },
    xLarge: {
      type: Boolean,
      default: false,
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['clear-messages', 'click', 'message-closed'],

  data() {
    return {
      menu: false,
    };
  },

  created() {
    if (this.defaultToOpen) {
      // Give framework a chance to know where this should render before showing it
      this.$nextTick(() => {
        this.menu = true;
      });
    }
  },

  computed: {
    title() {
      return this.popoverTitle || this.label;
    },

    localTitleColor() {
      return this.titleColor || this.color;
    },

    chipLabel() {
      return this.suppressPopover ? this.label : `${this.label}...`;
    },

    /**
     * Only allow hover on desktop and prop
     * @returns {Boolean}
     */
    openOnHover() {
      return this.isMobile ? !this.isMobile : this.hover;
    },

    shownMessages() {
      return typeof this.messages === 'string' ? [this.messages] : this.messages;
    },

    size() {
      if (this.xSmall) return 'x-small';
      if (this.small) return 'small';
      if (this.large) return 'large';
      if (this.xLarge) return 'x-large';
      return 'default'; // 'default' is Vuetify's default size value.
    },

    ...mapGetters({ isMobile: 'auth/isMobile' }),
  },

  methods: {
    isExtendedMessage(message) {
      return message instanceof Array && message.length > 2;
    },

    /**
     * Emitted when the 'Dismiss' button was pressed within a popover.
     */
    clearMessages() {
      this.menu = false;
      this.$emit('clear-messages');
    },

    /**
     * Emitted when a chip's 'x' icon is clicked. Parent must deal with repercussion of bound data.
     */
    messageClosed() {
      this.$emit('message-closed');
    },

    emitClick() {
      this.$emit('click');
    },
  },
};
</script>

<style lang="scss" scoped>
  .list-scroll {
    overflow-y: auto;
  }

  .break-word {
    word-break: break-word;
  }
</style>
